/**
 * Subscribers Help Page - Learning Guide
 *
 * Comprehensive guide to understanding and using the subscription system
 */

import { ErrorBoundary } from './components'
import { SignalIcon } from '@bizfirst/common-react'

export function SubscribersHelpPage() {
  return (
    <ErrorBoundary>
      <div className="page-container hooks-subscribers-page">
        <div className="page-header">
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><SignalIcon /> Understanding Message Subscriptions</h1>
        <p className="page-description">
          Learn how to subscribe to messages, implement handlers, and build real-time applications with EdgeStream
        </p>
      </div>

      {/* What Are Subscriptions */}
      <section className="section">
        <h2>What Are Subscriptions?</h2>
        <p>
          Subscriptions are the way your application listens for messages flowing through EdgeStream. Think of them as event
          listeners that wake up when messages matching your topic pattern arrive.
        </p>
        <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.4)', padding: 'var(--spacing-lg)', borderRadius: '12px', marginTop: 'var(--spacing-md)' }}>
          <code style={{ display: 'block', color: '#a8d8ea' }}>
            stream.subscribe('demo', 'user.created', (envelope) =&gt; {'{}'}<br />
            &nbsp;&nbsp;console.log('New user:', envelope.data);<br />
            {'}'})
          </code>
        </div>
      </section>

      {/* Topic Patterns */}
      <section className="section">
        <h2>Topic Pattern Matching</h2>
        <p>Subscriptions support flexible pattern matching for routing messages:</p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--spacing-lg)', marginTop: 'var(--spacing-md)' }}>
          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.4)', padding: 'var(--spacing-lg)', borderRadius: '12px' }}>
            <h4 style={{ margin: '0 0 var(--spacing-md) 0', color: 'var(--color-primary)' }}>Wildcard: *</h4>
            <p style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--text-secondary)' }}>Subscribes to ALL messages</p>
            <code style={{ background: 'var(--bg-dark)', padding: '8px', borderRadius: '4px', color: '#a8d8ea', fontSize: '12px', display: 'block' }}>
              subscribe('*', handler)
            </code>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.4)', padding: 'var(--spacing-lg)', borderRadius: '12px' }}>
            <h4 style={{ margin: '0 0 var(--spacing-md) 0', color: 'var(--color-primary)' }}>Exact Match: topic.subtopic</h4>
            <p style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--text-secondary)' }}>Listen to specific topic</p>
            <code style={{ background: 'var(--bg-dark)', padding: '8px', borderRadius: '4px', color: '#a8d8ea', fontSize: '12px', display: 'block' }}>
              subscribe('user.created', handler)
            </code>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.4)', padding: 'var(--spacing-lg)', borderRadius: '12px' }}>
            <h4 style={{ margin: '0 0 var(--spacing-md) 0', color: 'var(--color-primary)' }}>Wildcard Suffix: topic.*</h4>
            <p style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--text-secondary)' }}>Listen to all subtopics</p>
            <code style={{ background: 'var(--bg-dark)', padding: '8px', borderRadius: '4px', color: '#a8d8ea', fontSize: '12px', display: 'block' }}>
              subscribe('user.*', handler)
            </code>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.4)', padding: 'var(--spacing-lg)', borderRadius: '12px' }}>
            <h4 style={{ margin: '0 0 var(--spacing-md) 0', color: 'var(--color-primary)' }}>Nested Wildcard: topic.sub.*</h4>
            <p style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--text-secondary)' }}>Listen to nested patterns</p>
            <code style={{ background: 'var(--bg-dark)', padding: '8px', borderRadius: '4px', color: '#a8d8ea', fontSize: '12px', display: 'block' }}>
              subscribe('data.user.*', handler)
            </code>
          </div>
        </div>
      </section>

      {/* Common Topics */}
      <section className="section">
        <h2>Common Topic Hierarchies</h2>
        <p>These are standard topics you'll encounter when building EdgeStream applications:</p>

        <div style={{ marginTop: 'var(--spacing-md)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: 'var(--bg-tertiary)' }}>
              <tr>
                <th style={{ padding: 'var(--spacing-md)', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Category</th>
                <th style={{ padding: 'var(--spacing-md)', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Topics</th>
                <th style={{ padding: 'var(--spacing-md)', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: 'var(--spacing-md)' }}>Forms</td>
                <td style={{ padding: 'var(--spacing-md)' }}>
                  <code style={{ background: 'var(--bg-tertiary)', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>form.*</code>
                </td>
                <td style={{ padding: 'var(--spacing-md)', color: 'var(--text-secondary)' }}>Form events (open, submit, close, validate)</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: 'var(--spacing-md)' }}>User Data</td>
                <td style={{ padding: 'var(--spacing-md)' }}>
                  <code style={{ background: 'var(--bg-tertiary)', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>data.user.*</code>
                </td>
                <td style={{ padding: 'var(--spacing-md)', color: 'var(--text-secondary)' }}>User create, update, delete events</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: 'var(--spacing-md)' }}>Pipeline</td>
                <td style={{ padding: 'var(--spacing-md)' }}>
                  <code style={{ background: 'var(--bg-tertiary)', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>pipeline.*</code>
                </td>
                <td style={{ padding: 'var(--spacing-md)', color: 'var(--text-secondary)' }}>Pipeline execution start, complete, error</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: 'var(--spacing-md)' }}>Messages</td>
                <td style={{ padding: 'var(--spacing-md)' }}>
                  <code style={{ background: 'var(--bg-tertiary)', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>message.*</code>
                </td>
                <td style={{ padding: 'var(--spacing-md)', color: 'var(--text-secondary)' }}>Message sent, received, routed</td>
              </tr>
              <tr>
                <td style={{ padding: 'var(--spacing-md)' }}>Errors</td>
                <td style={{ padding: 'var(--spacing-md)' }}>
                  <code style={{ background: 'var(--bg-tertiary)', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>error.*</code>
                </td>
                <td style={{ padding: 'var(--spacing-md)', color: 'var(--text-secondary)' }}>Exceptions, validation failures, timeouts</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Best Practices */}
      <section className="section">
        <h2>Best Practices</h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--spacing-lg)', marginTop: 'var(--spacing-md)' }}>
          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', borderLeft: '4px solid var(--obs-success)', padding: 'var(--spacing-lg)', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--obs-success)' }}>Be Specific</h4>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
              Prefer specific topics over wildcards. This improves performance and makes your code maintainable.
            </p>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', borderLeft: '4px solid var(--obs-success)', padding: 'var(--spacing-lg)', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--obs-success)' }}>Always Unsubscribe</h4>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
              Unsubscribe when done to prevent memory leaks and improve performance.
            </p>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', borderLeft: '4px solid var(--obs-success)', padding: 'var(--spacing-lg)', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--obs-success)' }}>Handle Errors</h4>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
              Wrap callback logic in try-catch to prevent unhandled exceptions.
            </p>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', borderLeft: '4px solid var(--obs-warning)', padding: 'var(--spacing-lg)', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--obs-warning)' }}>Avoid Heavy Operations</h4>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
              Don't perform expensive computations in callbacks. Offload to background tasks.
            </p>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', borderLeft: '4px solid var(--obs-warning)', padding: 'var(--spacing-lg)', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--obs-warning)' }}>Use Wildcards Sparingly</h4>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
              Wildcard subscriptions (*) catch all messages. Use sparingly for performance.
            </p>
          </div>

          <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.3)', borderLeft: '4px solid var(--obs-warning)', padding: 'var(--spacing-lg)', borderRadius: '4px' }}>
            <h4 style={{ margin: '0 0 var(--spacing-sm) 0', color: 'var(--obs-warning)' }}>Avoid Loops</h4>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
              Don't publish messages from within subscription callbacks for the same topic.
            </p>
          </div>
        </div>
      </section>

      {/* API Reference */}
      <section className="section">
        <h2>API Reference</h2>

        <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.4)', padding: 'var(--spacing-lg)', borderRadius: '12px', marginTop: 'var(--spacing-md)' }}>
          <h4 style={{ margin: '0 0 var(--spacing-md) 0', color: 'var(--color-primary)' }}>Subscribe to Messages</h4>
          <pre style={{ margin: 0, overflow: 'auto', color: '#a8d8ea' }}>
            <code>{`const subscription = streamInstance.subscribe(
  'serverId',
  'topic.pattern',
  (envelope) => {
    // Handle message
    console.log('Received:', envelope.data);
  }
);`}</code>
          </pre>
        </div>

        <div style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(58, 140, 69, 0.4)', padding: 'var(--spacing-lg)', borderRadius: '12px', marginTop: 'var(--spacing-md)' }}>
          <h4 style={{ margin: '0 0 var(--spacing-md) 0', color: 'var(--color-primary)' }}>Unsubscribe</h4>
          <pre style={{ margin: 0, overflow: 'auto', color: '#a8d8ea' }}>
            <code>{`// Stop listening
subscription.unsubscribe();

// Check if still active
if (!subscription.active) {
  console.log('Unsubscribed successfully');
}`}</code>
          </pre>
          </div>
        </section>
      </div>
    </ErrorBoundary>
  );
}
